import {
  HttpClient,
  HttpErrorResponse,
  HttpParameterCodec,
  HttpParams
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BaseDto } from '@up/data';
import { BaseBulkRequestDto } from '@up/data';
import { MerchantDto } from '@up/data';
import { PagedListResponseDto } from '@up/data';
import { ENV_OPTIONS } from "@up/shared/injection-tokens";

export class HttpUrlEncodingCodec implements HttpParameterCodec {
  encodeKey(k: string): string {
    return standardEncoding(k);
  }
  encodeValue(v: string): string {
    return standardEncoding(v);
  }
  decodeKey(k: string): string {
    return decodeURIComponent(k);
  }
  decodeValue(v: string) {
    return decodeURIComponent(v);
  }
}
function standardEncoding(v: string): string {
  return v;
}

@Injectable({
  providedIn: 'root'
})
export class BaseService<T> {
  protected baseApiUrl = `${this.environmentOptions.apiUrl}/`;
  protected apiEndPoint = ``;
  public title = ``;

  constructor(@Inject(ENV_OPTIONS) private environmentOptions: any,
              protected httpClient: HttpClient) { }

  protected getFullUrl(): string {
    return this.baseApiUrl + this.apiEndPoint;
  }

  protected prepareRelationshipParams(
    relationships: string[],
    disableCodec = false
  ): HttpParams {
    let params = disableCodec
      ? new HttpParams({ encoder: new HttpUrlEncodingCodec() })
      : new HttpParams();

    if (relationships !== null) {
      relationships.forEach((v) => {
        params = params.append('join', v);
      });
    }
    return params;
  }

  public getByMerchantId(
    merchant: MerchantDto,
    relationships: string[] = []
  ): Observable<T[]> {
    let id = '';
    id = merchant.id.toString();
    const params = this.prepareRelationshipParams(relationships);
    return this.httpClient
      .get<T[]>(this.getFullUrl() + `/merchantId/${id}`, { params: params })
      .pipe(
        map((data) => data),
        catchError(this.handleError)
      );
  }

  public getMany(
    relationships: string[] = null,
    appendToUrl: string = '',
    appendParams: HttpParams = null
  ): Observable<T[]> {
    let params = this.prepareRelationshipParams(
      relationships,
      appendParams !== null
    );
    if (appendParams) {
      appendParams.keys().forEach((k) => {
        if (appendParams.getAll(k).length > 1) {
          appendParams.getAll(k).forEach((v) => {
            params = params.append(k, v);
          });
        } else {
          params = params.append(k, appendParams.get(k));
        }
      });
    }
    return this.httpClient
      .get<T[]>(this.getFullUrl() + appendToUrl, {
        params
      })
      .pipe(
        map((data) => data),
        catchError(this.handleError)
      );
  }

  public get(id: string, relationships: string[] = null): Observable<T> {
    return this.httpClient
      .get<T>(this.getFullUrl() + `/${id}`, {
        params: this.prepareRelationshipParams(relationships)
      })
      .pipe(
        map((data) => data),
        catchError(this.handleError)
      );
  }

  public save(record: T, relationships: string[] = null) {
    if (((record as unknown) as BaseDto).id) {
      return this.put(record, relationships);
    }
    return this.post(record, relationships);
  }

  public saveMany(records: T[], relationships: string[] = []): Observable<T[]> {
    return this.httpClient
      .post<T[]>(
        this.getFullUrl() + '/bulk',
        {
          bulk: records
        },
        { params: this.prepareRelationshipParams(relationships) }
      )
      .pipe(catchError(this.handleError));
  }

  public post(record: T, relationships: string[] = null) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    return this.httpClient
      .post<T>(this.getFullUrl(), record, {
        params: this.prepareRelationshipParams(relationships)
      })
      .pipe(catchError(this.handleError));
  }

  public put(record: T, relationships: string[] = null) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    const id = ((record as unknown) as BaseDto).id;

    return this.httpClient
      .put<T>(this.getFullUrl() + '/' + id, record, {
        params: this.prepareRelationshipParams(relationships)
      })
      .pipe(catchError(this.handleError));
  }

  public delete(record: T) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');
    const id = ((record as unknown) as BaseDto).id;
    return this.httpClient
      .delete<T>(this.getFullUrl() + '/' + id)
      .pipe(catchError(this.handleError));
  }

  public getManyPaged(
    limit = 10,
    page = 1,
    relationships?: string[],
    sorts?: string[],
    filters?: string[],
    disableCodec: boolean = false
  ): Observable<PagedListResponseDto> {
    return this.httpClient
      .get<PagedListResponseDto>(this.getFullUrl(), {
        params: this.preparePagingParams(
          limit,
          page,
          relationships,
          sorts,
          filters,
          disableCodec
        )
      })
      .pipe(
        map((data) => data),
        catchError(this.handleError)
      );
  }

  public bulk(records: T[]) {
    const headers = new Headers();
    headers.append('Content-Type', 'application/json');

    const bulkRequest = new BaseBulkRequestDto(records);

    return this.httpClient
      .post<T[]>(this.getFullUrl() + '/bulk', bulkRequest, {})
      .pipe(catchError(this.handleError));
  }

  protected preparePagingParams(
    limit: number = 20,
    page: number = 1,
    relationships?: string[],
    sorts?: string[],
    filters?: string[],
    disableCodec: boolean = false
  ): HttpParams {
    let params = disableCodec
      ? new HttpParams({ encoder: new HttpUrlEncodingCodec() })
      : new HttpParams();

    if (relationships) {
      relationships.forEach((join) => {
        params = params.append('join', join);
      });
    }

    if (sorts) {
      sorts.forEach((sort) => {
        params = params.append('sort', sort);
      });
    }

    if (filters) {
      filters.forEach((filter) => {
        params = params.append('filter', filter);
      });
    }

    params = params.append('limit', limit.toString());
    params = params.append('page', page.toString());
    return params;
  }

  protected handleError(res: HttpErrorResponse | any) {
    console.error(res.error || res.body.error);
    return observableThrowError(res.error || 'Server error');
  }
}
